import React, { useState, useCallback, useRef } from 'react';
import { Plus, Upload, X, Camera, MapPin, Tag, ArrowRight, ArrowLeft } from 'lucide-react';
import { Location, LocationCategory } from '../types';
import { Map } from './Map';
import toast from 'react-hot-toast';
import { uploadImages } from '../utils/images';
import { validateImageFile } from '../utils/images';
import { testStorageAccess } from '../utils/supabase';

interface AddLocationProps {
  onAdd: (location: Omit<Location, 'id'>) => Promise<void>;
  onClose: () => void;
}

export const AddLocation: React.FC<AddLocationProps> = ({ onAdd, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    category: 'eco-trail' as LocationCategory,
    coordinates: { lat: 53.9, lng: 27.5667 },
    tags: [] as string[],
  });

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'location' | 'media'>('info');

  const categories: { value: LocationCategory; label: string }[] = [
    { value: 'eco-trail', label: 'Экасцежка' },
    { value: 'camping', label: 'Кемпінг' },
    { value: 'stargazing', label: 'Назіранне за зоркамі' },
    { value: 'picnic', label: 'Пікнік' },
    { value: 'eco-hotel', label: 'Экаатэль' },
    { value: 'estate', label: 'Сядзіба' },
    { value: 'zoo', label: 'Звярынцы' },
    { value: 'Historical places', label: 'Гістарычныя месцы' },
    { value: 'other', label: 'Іншае' }
  ];

  const handleImageAdd = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    for (const file of files) {
      try {
        validateImageFile(file);
        
        // Create a new File object with a unique name to prevent caching issues
        const timestamp = Date.now();
        const newFile = new File([file], `${timestamp}_${file.name}`, {
          type: file.type,
          lastModified: file.lastModified,
        });
        
        // Create object URL for preview
        const previewUrl = URL.createObjectURL(newFile);
        
        // Update state
        setImagePreviewUrls(prev => [...prev, previewUrl]);
        setImageFiles(prev => [...prev, newFile]);
        
        toast.success('Фота паспяхова дададзена');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Памылка пры даданні файла');
      }
    }
  }, []);

  const handleImageClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleImageRemove = useCallback((index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    toast.success('Фота выдалена');
  }, [imagePreviewUrls]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Калі ласка, увядзіце назву');
      setActiveTab('info');
      return;
    }

    if (!formData.shortDescription.trim()) {
      toast.error('Калі ласка, увядзіце кароткае апісанне');
      setActiveTab('info');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Калі ласка, увядзіце поўнае апісанне');
      setActiveTab('info');
      return;
    }

    if (imageFiles.length === 0) {
      toast.error('Калі ласка, дадайце хаця б адну фатаграфію');
      setActiveTab('media');
      return;
    }

    setIsSubmitting(true);

    try {
      // Test storage access first
      console.log('Testing storage access...');
      const hasStorageAccess = await testStorageAccess();
      if (!hasStorageAccess) {
        throw new Error('Няма доступу да сховішча. Калі ласка, праверце налады.');
      }

      // Upload images to Supabase Storage and get public URLs
      console.log('Uploading images to Supabase Storage...');
      const uploadedImageUrls = await uploadImages(imageFiles);
      console.log('Images uploaded successfully, URLs:', uploadedImageUrls);

      // Validate that all URLs are valid
      const validUrls = uploadedImageUrls.filter(url => 
        url && typeof url === 'string' && url.startsWith('http')
      );

      if (validUrls.length !== uploadedImageUrls.length) {
        throw new Error('Некаторыя изображения не были загружены правильно');
      }

      // Create location with uploaded image URLs (full public URLs)
      await onAdd({
        name: formData.name,
        description: formData.description,
        shortDescription: formData.shortDescription,
        category: formData.category,
        coordinates: formData.coordinates,
        tags: formData.tags,
        images: validUrls // These are full public URLs from Supabase Storage
      });

      onClose();
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      toast.error(err instanceof Error ? err.message : 'Не атрымалася дадаць лакацыю');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const validateFirstTab = () => {
    if (formData.name.trim().length < 2) {
      toast.error('Назва павінна змяшчаць не менш за 2 сімвалы');
      return false;
    }
    if (formData.shortDescription.trim().length < 10) {
      toast.error('Кароткае апісанне павінна змяшчаць не менш за 10 сімвалаў');
      return false;
    }
    if (formData.description.trim().length < 20) {
      toast.error('Поўнае апісанне павінна змяшчаць не менш за 20 сімвалаў');
      return false;
    }
    return true;
  };

  const handleNextTab = () => {
    if (activeTab === 'info') {
      if (!validateFirstTab()) {
        return;
      }
      setActiveTab('location');
    } else if (activeTab === 'location') {
      setActiveTab('media');
    }
  };

  const handlePrevTab = () => {
    if (activeTab === 'media') setActiveTab('location');
    else if (activeTab === 'location') setActiveTab('info');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">Дадаць новую лакацыю</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setActiveTab('info')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-2 sm:px-4 transition-colors ${
              activeTab === 'info' 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1 sm:gap-2">
              <span className="w-5 h-5 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm">
                1
              </span>
              <Tag size={16} className="hidden sm:block" />
              <span className="text-sm sm:text-base">Інфа</span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('location')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-2 sm:px-4 transition-colors ${
              activeTab === 'location' 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1 sm:gap-2">
              <span className="w-5 h-5 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm">
                2
              </span>
              <MapPin size={16} className="hidden sm:block" />
              <span className="text-sm sm:text-base">Месца</span>
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('media')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-2 sm:px-4 transition-colors ${
              activeTab === 'media' 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1 sm:gap-2">
              <span className="w-5 h-5 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-sm">
                3
              </span>
              <Camera size={16} className="hidden sm:block" />
              <span className="text-sm sm:text-base">Фота</span>
            </span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div className="p-4 space-y-4">
            {activeTab === 'info' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Назва
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Мінімум 2 сімвалы
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Кароткае апісанне
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={formData.shortDescription}
                    onChange={e => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Мінімум 10 сімвалаў
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Поўнае апісанне
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Мінімум 20 сімвалаў
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Катэгорыя
                  </label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData(prev => ({ ...prev, category: e.target.value as LocationCategory }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Хэштэгі
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            tags: prev.tags.filter(t => t !== tag)
                          }))}
                          className="ml-1 text-gray-500 hover:text-gray-700"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleTagAdd())}
                      placeholder="Дадайце хэштэг..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={handleTagAdd}
                      className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'location' && (
              <div className="h-[400px] rounded-xl overflow-hidden">
                <Map
                  center={[formData.coordinates.lat, formData.coordinates.lng]}
                  onClick={(lat, lng) => setFormData(prev => ({
                    ...prev,
                    coordinates: { lat, lng }
                  }))}
                  withSearch={true}
                />
              </div>
            )}

            {activeTab === 'media' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Фотаздымкі
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {imagePreviewUrls.map((previewUrl, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={previewUrl}
                        alt={`Фота ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="absolute top-1 right-1 p-1 bg-white rounded-full shadow-md"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {imagePreviewUrls.length < 5 && (
                    <button
                      type="button"
                      onClick={handleImageClick}
                      className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageAdd}
                        multiple
                      />
                      <Upload className="text-gray-400 mb-2" size={24} />
                      <span className="text-sm text-gray-500">Дадаць фота</span>
                    </button>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Максімальны памер файла: 5 МБ. Дадайце хаця б адну фатаграфію.
                </p>
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-gray-50 sticky bottom-0">
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-between">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm sm:text-base"
                  disabled={isSubmitting}
                >
                  Адмяніць
                </button>
                {activeTab !== 'info' && (
                  <button
                    type="button"
                    onClick={handlePrevTab}
                    className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                  >
                    <ArrowLeft size={18} />
                    <span>Назад</span>
                  </button>
                )}
              </div>
              <div>
                {activeTab !== 'media' ? (
                  <button
                    type="button"
                    onClick={handleNextTab}
                    className="px-3 sm:px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                  >
                    <span>Далей</span>
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="px-3 sm:px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Захаванне...</span>
                      </>
                    ) : (
                      <span>Захаваць</span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};